import CustomDropdown from '../../reusable/dropdown';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { d7ProspectSearchSchema } from '../../../schema/prospect_schema';
import LabelWrapper from '../../wrapper/label';
import { SearchD7ProspectsParams } from '../../../params/prospect';
import { searchProspectsThunk } from '../../../redux/thunks/prospect';
import { useState } from 'react';
import { AppDispatch, RootState } from '../../../redux/store';
import { Bounce, toast } from 'react-toastify';
import { Box, Button, ClickAwayListener, MenuItem, Paper, Popper, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: FindPreospectsModal
 * @paragraph: A modal component for searching prospects.
 * Allows users to specify niche, city, state, and country for the prospect search.
 * @param: {boolean} open - Determines whether the modal is open.
 * @param: {function} onClose - Function to handle closing the modal.
 */

const FindD7ProspectsModal = ({ open, onClose, onSubmit }: { open: boolean; onClose: () => void, onSubmit: (name: string) => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * initialValues: {SearchProspectsParams} -  Initial form values for prospect search.
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders: An object containing the loader details from the Redux store.
     * countryValues: static list of countries for dropdown
     */
    // Initial form values for prospect search
    const initialValues: SearchD7ProspectsParams = {
        niche: '',
        city: '',
        state: '',
        country: '',
        type: 'd7'
    };
    const dispatch = useDispatch<AppDispatch>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { loaders, suggestions } = useSelector((state: RootState) => state.prospect);
    const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);

    /**
     * @heading3: Functions
     * @heading4: Handles the submission of the prospect search form.
     * @paragraph: Dispatches the searchProspectsThunk with the form values.
     *
     * @param: {SearchProspectsParams} values - The form values entered by the user.
     */
    const handleSubmit = async (values: SearchD7ProspectsParams, { setFieldError }: import('formik').FormikHelpers<SearchD7ProspectsParams>) => {
        if (!suggestions.includes(values.niche)) {
            setFieldError('niche', 'Please select a valid niche from the suggestions.');
            toast.error('Select a valid niche from the suggestions.', {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                theme: 'dark',
                transition: Bounce
            });
            return;
        }
        try {
            await dispatch(searchProspectsThunk({
                niche: values.niche,
                city: values.city,
                state: values.state,
                country: values.country,
                type: 'd7'
            })).unwrap();
            onSubmit(`${values.niche} located in ${values.city}, ${values.state}`);
            // Close the modal after successful search
            onClose();
        } catch (e) {
            // Catch errors
        }
    };

    const handleNicheChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        setFieldValue: (field: string, value: string, shouldValidate?: boolean | undefined) => void,
        setFieldError: (field: string, message: string | undefined) => void
    ) => {
        const { value } = event.target;
        setFieldValue('niche', value);
        setFieldError('niche', '');

        if (value.length > 0) {
            const filtered = suggestions.filter((item: string) => item.toLowerCase().includes(value.toLowerCase()));
            setFilteredSuggestions(filtered);
            setAnchorEl(event.currentTarget);
        } else {
            setFilteredSuggestions([]);
            setAnchorEl(null);
        }
    };

    const handleSelectNiche = (
        niche: string,
        setFieldValue: (field: string, value: string, shouldValidate?: boolean | undefined) => void,
        setFieldError: (field: string, message: string | undefined) => void
    ) => {
        setFieldValue('niche', niche);
        setFieldError('niche', '');
        setFilteredSuggestions([]);
        setAnchorEl(null);
    };

    const handleClickAway = () => {
        setFilteredSuggestions([]);
        setAnchorEl(null);
    };

    // Dropdown options for country selection
    const countyValues = [
        { value: 'US', label: 'USA' },
        { value: 'CA', label: 'Canada' },
        { value: 'AUS', label: 'Australia' },
        { value: 'GB', label: 'United Kingdom' }
    ];

    return <CustomModal open={open} handleClose={loaders.isSearching ? () => null : onClose} title="Find Source 1 Prospects">
        <Formik initialValues={initialValues} validationSchema={d7ProspectSearchSchema} onSubmit={handleSubmit}>
            {({ values, handleChange, handleBlur, setFieldValue, errors, touched, setFieldError }) => (
                <Form>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {/* Instructional text */}
                        <Typography variant="body1" color="rgba(51,56,63,1)" fontSize="16px" fontWeight={500}>Please fill all the fields to enable the prospect search</Typography>
                        {/* Input field for Niche */}
                        <Box position="relative">
                            <CustomInput
                                label="Niche"
                                placeholder="Niche"
                                fullWidth
                                variant="outlined"
                                value={values.niche}
                                onChange={(event) => handleNicheChange(event, setFieldValue, setFieldError)}
                                onBlur={handleBlur('niche')}
                                error={touched.niche && errors.niche}
                                disabled={loaders.isSearching}
                            />

                            {/* Popper for Suggestions */}
                            <Popper open={Boolean(anchorEl) && filteredSuggestions.length > 0} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1500 }}>
                                <ClickAwayListener onClickAway={handleClickAway}>
                                    <Paper elevation={3} sx={{ width: anchorEl ? anchorEl.clientWidth : 'auto', maxHeight: '200px', overflowY: 'auto' }}>
                                        {filteredSuggestions.map((suggestion, index) => (
                                            <MenuItem key={index} onClick={() => handleSelectNiche(suggestion, setFieldValue, setFieldError)}>
                                                {suggestion}
                                            </MenuItem>
                                        ))}
                                    </Paper>
                                </ClickAwayListener>
                            </Popper>
                        </Box>
                        {/* Input field for City */}
                        <CustomInput
                            label="City"
                            placeholder="City"
                            fullWidth
                            value={values.city}
                            onChange={handleChange('city')}
                            onBlur={handleBlur('city')}
                            error={touched.city && errors.city}
                            disabled={loaders.isSearching}
                        />
                        {/* Input field for State/Region */}
                        <Box>
                            <CustomInput
                                label="State/Region"
                                placeholder="State/Region"
                                fullWidth
                                value={values.state}
                                onChange={handleChange('state')}
                                onBlur={handleBlur('state')}
                                error={touched.state && errors.state}
                                disabled={loaders.isSearching}
                            />
                            {/* Hint for State/Region input */}
                            <Typography color="rgba(111,118,126,1)" variant="body2" fontSize="12px" fontWeight={400}>Ex. CA, TX for USA and Full State/Region names for other countries</Typography>
                        </Box>
                        {/* Dropdown for Country selection */}
                        <Box width="100%" boxSizing="border-box">
                            <LabelWrapper label="Country" />
                            <Box boxSizing="border-box">{
                                <CustomDropdown
                                    options={countyValues}
                                    value={values.country}
                                    onChange={(event) => setFieldValue('country', event.target.value)}
                                    onBlur={handleBlur('country')}
                                    error={Boolean(touched.country && errors.country)}
                                    disabled={loaders.isSearching}
                                />
                            }</Box>
                        </Box>
                        {/* Action buttons */}
                        <Box display="flex" gap={2}>
                            <Button variant="outlined" color="primary" onClick={onClose} fullWidth disabled={loaders.isSearching}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loaders.isSearching}>
                                {loaders.isSearching ? 'Searching...' : 'Search Prospects'}
                            </Button>
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    </CustomModal>;
};

export default FindD7ProspectsModal;